import React, { useEffect, useState } from 'react';
import admissionsService from '../services/admissionsService';
import { toast } from 'sonner';
import DialogModal from './DialogModal';

const EMPTY_INTERVIEW = { applicant_id: '', scheduled_at: '', interviewer: '' };
const EMPTY_OFFER = { applicant_id: '', class_id: '', offered_date: '', expiry_date: '', terms: '' };
const EMPTY_DEPOSIT = { applicant_id: '', required_amount: '', due_date: '' };
const EMPTY_EXAM = { applicant_id: '', exam_name: '', exam_date: '', subject: '', score: '', max_score: '100', grade: '', notes: '' };

const OFFER_TRANSITIONS = { draft: ['sent'], sent: ['accepted', 'declined', 'expired'] };

const REJECTION_REASON_CODES = [
  ['academic_requirements_not_met', 'Academic requirements not met'],
  ['class_full', 'Class is full'],
  ['incomplete_documentation', 'Incomplete documentation'],
  ['failed_entrance_exam', 'Failed entrance exam'],
  ['failed_interview', 'Failed interview'],
  ['age_ineligible', 'Age ineligible'],
  ['other', 'Other'],
];

const WITHDRAWAL_REASON_CODES = [
  ['chose_another_school', 'Chose another school'],
  ['financial', 'Financial'],
  ['relocation', 'Relocation'],
  ['declined_offer', 'Declined offer'],
  ['no_longer_interested', 'No longer interested'],
  ['other', 'Other'],
];

function EnterpriseAdmissionsPanel({ applicants, classes = [], schoolCode }) {
  const [tab, setTab] = useState('analytics');
  const [data, setData] = useState({ analytics: null, waitlist: [], interviews: [], offers: [], deposits: [], exams: [] });
  const [selectedApplicant, setSelectedApplicant] = useState('');
  const [rejectCode, setRejectCode] = useState('');
  const [withdrawCode, setWithdrawCode] = useState('');
  const [interviewForm, setInterviewForm] = useState(EMPTY_INTERVIEW);
  const [offerForm, setOfferForm] = useState(EMPTY_OFFER);
  const [depositForm, setDepositForm] = useState(EMPTY_DEPOSIT);
  const [examForm, setExamForm] = useState(EMPTY_EXAM);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  const load = async () => {
    try {
      const [analytics, waitlist, interviews, offers, deposits, exams] = await Promise.all([
        admissionsService.analytics(),
        admissionsService.waitlist(),
        admissionsService.interviews(),
        admissionsService.offers(),
        admissionsService.deposits(),
        admissionsService.examResults(),
      ]);
      setData({ analytics, waitlist: waitlist || [], interviews: interviews || [], offers: offers || [], deposits: deposits || [], exams: exams || [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load admissions controls');
    }
  };
  useEffect(() => { load(); }, []);

  const applicantName = id => { const a = applicants.find(item => item.id === id); return a ? `${a.first_name} ${a.last_name}` : (id || '-'); };
  const className = id => { const c = classes.find(item => item.id === id); return c ? (c.class_name || c.name) : (id || '-'); };

  const stage = (status, reasonCode) => {
    if (!selectedApplicant) return;
    setPromptDialog({
      title: `Reason for ${status.replace('_', ' ')}`,
      message: `Reason for ${status.replace('_', ' ')}:`,
      defaultValue: '',
      onSubmit: async (reason) => {
        if (!reason?.trim()) { toast.error('A reason is required'); return; }
        try {
          await admissionsService.changeStage(selectedApplicant, { status, reason: reason.trim(), reason_code: reasonCode || null });
          await load();
          toast.success('Applicant stage updated');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Unable to update stage');
        }
      },
    });
  };

  const promoteApplicant = async waitlistEntry => {
    try {
      await admissionsService.promoteFromWaitlist(waitlistEntry.applicant_id);
      await load();
      toast.success(`${waitlistEntry.name} promoted off the waitlist`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to promote applicant');
    }
  };

  const reorderWaitlistEntry = async (entry, newRank) => {
    if (newRank < 1) return;
    try {
      await admissionsService.reorderWaitlist(entry.applicant_id, newRank);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to reorder waitlist');
    }
  };

  const submitInterview = async event => {
    event.preventDefault();
    if (!interviewForm.applicant_id || !interviewForm.scheduled_at) { toast.error('Select an applicant and a time'); return; }
    try {
      await admissionsService.createInterview(interviewForm);
      setInterviewForm(EMPTY_INTERVIEW);
      await load();
      toast.success('Interview scheduled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to schedule interview');
    }
  };

  const recordInterviewResult = interview => {
    setPromptDialog({
      title: 'Interview Score',
      message: 'Interview score (0-100, leave blank for none):',
      defaultValue: interview.score != null ? String(interview.score) : '',
      onSubmit: (scoreInput) => {
        // Defer opening the second prompt until after this dialog's own onClose(),
        // which fires synchronously right after this callback and would otherwise
        // immediately clear the promptDialog state we're about to set here.
        setTimeout(() => {
          setPromptDialog({
            title: 'Interview Result',
            message: 'Result (pending/passed/failed/hold):',
            defaultValue: interview.result || 'pending',
            onSubmit: async (result) => {
              if (!result?.trim()) return;
              try {
                await admissionsService.updateInterview(interview.id, { score: scoreInput.trim() ? Number(scoreInput) : null, feedback: interview.feedback, result: result.trim() });
                await load();
                toast.success('Interview updated');
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Unable to update interview');
              }
            },
          });
        }, 0);
      },
    });
  };

  const submitOffer = async event => {
    event.preventDefault();
    if (!offerForm.applicant_id || !offerForm.offered_date) { toast.error('Select an applicant and an offer date'); return; }
    try {
      await admissionsService.createOffer({ ...offerForm, class_id: offerForm.class_id || null, expiry_date: offerForm.expiry_date || null, terms: offerForm.terms || null });
      setOfferForm(EMPTY_OFFER);
      await load();
      toast.success('Offer created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to create offer');
    }
  };

  const updateOfferStatus = async (offer, status) => {
    try {
      await admissionsService.updateOffer(offer.id, { status });
      await load();
      toast.success('Offer updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to update offer');
    }
  };

  const submitDeposit = async event => {
    event.preventDefault();
    if (!depositForm.applicant_id || !depositForm.required_amount) { toast.error('Select an applicant and an amount'); return; }
    try {
      await admissionsService.createDeposit({ ...depositForm, required_amount: Number(depositForm.required_amount), due_date: depositForm.due_date || null });
      setDepositForm(EMPTY_DEPOSIT);
      await load();
      toast.success('Deposit requirement created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to create deposit');
    }
  };

  const recordDepositPayment = deposit => {
    setPromptDialog({
      title: 'Record Payment',
      message: `Payment amount (balance: ${deposit.balance}):`,
      defaultValue: '',
      onSubmit: async (amountInput) => {
        const amount = Number(amountInput);
        if (!amountInput || !amount || amount <= 0) return;
        try {
          await admissionsService.payDeposit(deposit.id, { amount });
          await load();
          toast.success('Payment recorded');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Unable to record payment');
        }
      },
    });
  };

  const waiveDeposit = deposit => {
    setPromptDialog({
      title: 'Waive Deposit',
      message: 'Reason for waiving this deposit (optional):',
      defaultValue: '',
      onSubmit: async (reasonInput) => {
        const reason = reasonInput || null;
        try {
          await admissionsService.waiveDeposit(deposit.id, { reason });
          await load();
          toast.success('Deposit waived');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Unable to waive deposit');
        }
      },
    });
  };

  const copyDepositLink = deposit => {
    if (!schoolCode) { toast.error('School code unavailable'); return; }
    const link = `${window.location.origin}/apply/${schoolCode}/deposit/${deposit.id}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Payment link copied'),
      () => toast.error('Unable to copy link'),
    );
  };

  const submitExam = async event => {
    event.preventDefault();
    if (!examForm.applicant_id || !examForm.exam_name || !examForm.exam_date || !examForm.subject || examForm.score === '') { toast.error('Fill in all required exam fields'); return; }
    try {
      await admissionsService.createExamResult({ ...examForm, score: Number(examForm.score), max_score: Number(examForm.max_score || 100), grade: examForm.grade || null, notes: examForm.notes || null });
      setExamForm(EMPTY_EXAM);
      await load();
      toast.success('Exam result recorded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to record exam result');
    }
  };

  const applicantSelect = (value, onChange) => (
    <select className="form-select form-select-sm" value={value} onChange={onChange} required>
      <option value="">Select applicant</option>
      {applicants.map(item => <option key={item.id} value={item.id}>{item.first_name} {item.last_name}</option>)}
    </select>
  );

  const tabs = [['analytics', 'Analytics'], ['waitlist', 'Waitlist'], ['interviews', 'Interviews'], ['offers', 'Offers'], ['deposits', 'Deposits'], ['exams', 'Entrance results']];

  return (
    <section className="card mt-4">
      <div className="card-header d-flex flex-wrap gap-2 align-items-center">
        <strong>Admissions operations</strong>
        <div className="ms-auto d-flex flex-wrap gap-1">
          {tabs.map(([key, label]) => (
            <button type="button" key={key} className={`btn btn-sm ${tab === key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="card-body">
        <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <select className="form-select" style={{ maxWidth: 240 }} value={selectedApplicant} onChange={e => setSelectedApplicant(e.target.value)}>
            <option value="">Select applicant for stage action</option>
            {applicants.map(item => <option key={item.id} value={item.id}>{item.first_name} {item.last_name}</option>)}
          </select>
          <button className="btn btn-outline-warning" onClick={() => stage('waitlisted')} disabled={!selectedApplicant}>Waitlist</button>
          <select className="form-select form-select-sm" style={{ maxWidth: 220 }} value={rejectCode} onChange={e => setRejectCode(e.target.value)}>
            <option value="">Rejection reason code (optional)</option>
            {REJECTION_REASON_CODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <button className="btn btn-outline-danger" onClick={() => stage('rejected', rejectCode)} disabled={!selectedApplicant}>Reject</button>
          <select className="form-select form-select-sm" style={{ maxWidth: 220 }} value={withdrawCode} onChange={e => setWithdrawCode(e.target.value)}>
            <option value="">Withdrawal reason code (optional)</option>
            {WITHDRAWAL_REASON_CODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <button className="btn btn-outline-secondary" onClick={() => stage('withdrawn', withdrawCode)} disabled={!selectedApplicant}>Withdraw</button>
        </div>

        {tab === 'analytics' && data.analytics && (
          <>
            <div className="row g-3 mb-4">
              {[
                ['Total applicants', data.analytics.total_applicants],
                ['Enrolled', data.analytics.by_status?.enrolled || 0],
                ['Offered', data.analytics.by_status?.offered || 0],
                ['Deposit balance', `GHS ${data.analytics.deposit_balance.toFixed(2)}`],
                ['Interview pass rate', data.analytics.interview_pass_rate != null ? `${data.analytics.interview_pass_rate}%` : 'No decided interviews yet'],
              ].map(([label, value]) => (
                <div className="col-md-3" key={label}><div className="border rounded p-3"><small className="text-muted d-block">{label}</small><strong>{value}</strong></div></div>
              ))}
            </div>

            <div className="row g-4">
              <div className="col-md-6">
                <h6>Intake by month</h6>
                <DataTable headers={['Month', 'Applications']} rows={(data.analytics.by_month || []).map(row => [row.month, row.count])} />
              </div>
              <div className="col-md-6">
                <h6>By class applied for</h6>
                <DataTable headers={['Class', 'Applicants']} rows={(data.analytics.by_class || []).map(row => [row.class_name, row.count])} />
              </div>
              <div className="col-md-6">
                <h6>By term applied for</h6>
                <DataTable headers={['Term', 'Applicants']} rows={(data.analytics.by_term || []).map(row => [row.term_name, row.count])} />
              </div>
              <div className="col-md-6">
                <h6>Rejection / withdrawal reasons</h6>
                <DataTable headers={['Reason', 'Count']} rows={(data.analytics.by_reason || []).map(row => [row.reason, row.count])} />
              </div>
            </div>
          </>
        )}

        {tab === 'waitlist' && (
          <DataTable
            headers={['Rank', 'Applicant', 'Class', 'Updated', 'Actions']}
            rows={data.waitlist.map((item, index) => [item.rank, item.name, className(item.class_id), new Date(item.updated_at).toLocaleDateString(),
              <div key={item.applicant_id} className="d-flex gap-1">
                <button type="button" className="btn btn-sm btn-outline-secondary" title="Move up" disabled={index === 0} onClick={() => reorderWaitlistEntry(item, item.rank - 1)}>&uarr;</button>
                <button type="button" className="btn btn-sm btn-outline-secondary" title="Move down" disabled={index === data.waitlist.length - 1} onClick={() => reorderWaitlistEntry(item, item.rank + 1)}>&darr;</button>
                <button type="button" className="btn btn-sm btn-outline-success" onClick={() => promoteApplicant(item)}>Promote</button>
              </div>])}
          />
        )}

        {tab === 'interviews' && (
          <>
            <form className="d-flex flex-wrap gap-2 align-items-end mb-3" onSubmit={submitInterview}>
              {applicantSelect(interviewForm.applicant_id, e => setInterviewForm({ ...interviewForm, applicant_id: e.target.value }))}
              <input required type="datetime-local" className="form-control form-control-sm" value={interviewForm.scheduled_at} onChange={e => setInterviewForm({ ...interviewForm, scheduled_at: e.target.value })} />
              <input className="form-control form-control-sm" placeholder="Interviewer" value={interviewForm.interviewer} onChange={e => setInterviewForm({ ...interviewForm, interviewer: e.target.value })} />
              <button className="btn btn-sm btn-primary" type="submit">Schedule interview</button>
            </form>
            <DataTable
              headers={['Applicant', 'Scheduled', 'Interviewer', 'Score', 'Result', 'Actions']}
              rows={data.interviews.map(item => [applicantName(item.applicant_id), item.scheduled_at, item.interviewer || '-', item.score ?? '-', item.result,
                <button key={item.id} type="button" className="btn btn-sm btn-outline-primary" onClick={() => recordInterviewResult(item)}>Record result</button>])}
            />
          </>
        )}

        {tab === 'offers' && (
          <>
            <form className="d-flex flex-wrap gap-2 align-items-end mb-3" onSubmit={submitOffer}>
              {applicantSelect(offerForm.applicant_id, e => setOfferForm({ ...offerForm, applicant_id: e.target.value }))}
              <select className="form-select form-select-sm" value={offerForm.class_id} onChange={e => setOfferForm({ ...offerForm, class_id: e.target.value })}>
                <option value="">No class</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.class_name || c.name}</option>)}
              </select>
              <input required type="date" className="form-control form-control-sm" value={offerForm.offered_date} onChange={e => setOfferForm({ ...offerForm, offered_date: e.target.value })} />
              <input type="date" className="form-control form-control-sm" placeholder="Expiry" value={offerForm.expiry_date} onChange={e => setOfferForm({ ...offerForm, expiry_date: e.target.value })} />
              <input className="form-control form-control-sm" placeholder="Terms" value={offerForm.terms} onChange={e => setOfferForm({ ...offerForm, terms: e.target.value })} />
              <button className="btn btn-sm btn-primary" type="submit">Create offer</button>
            </form>
            <DataTable
              headers={['Applicant', 'Class', 'Offered', 'Expires', 'Status', 'Actions']}
              rows={data.offers.map(item => [applicantName(item.applicant_id), className(item.class_id), item.offered_date, item.expiry_date || '-', item.status,
                <div key={item.id} className="d-flex gap-1">
                  {(OFFER_TRANSITIONS[item.status] || []).map(next => (
                    <button key={next} type="button" className="btn btn-sm btn-outline-secondary text-capitalize" onClick={() => updateOfferStatus(item, next)}>{next}</button>
                  ))}
                  {!(OFFER_TRANSITIONS[item.status] || []).length && '-'}
                </div>])}
            />
          </>
        )}

        {tab === 'deposits' && (
          <>
            <form className="d-flex flex-wrap gap-2 align-items-end mb-3" onSubmit={submitDeposit}>
              {applicantSelect(depositForm.applicant_id, e => setDepositForm({ ...depositForm, applicant_id: e.target.value }))}
              <input required type="number" step="0.01" min="0.01" className="form-control form-control-sm" placeholder="Required amount" value={depositForm.required_amount} onChange={e => setDepositForm({ ...depositForm, required_amount: e.target.value })} />
              <input type="date" className="form-control form-control-sm" placeholder="Due date" value={depositForm.due_date} onChange={e => setDepositForm({ ...depositForm, due_date: e.target.value })} />
              <button className="btn btn-sm btn-primary" type="submit">Create deposit</button>
            </form>
            <DataTable
              headers={['Applicant', 'Required', 'Paid', 'Balance', 'Status', 'Actions']}
              rows={data.deposits.map(item => [applicantName(item.applicant_id), item.required_amount, item.paid_amount, item.balance, item.status,
                (item.status === 'paid' || item.status === 'waived') ? '-' : (
                  <div key={item.id} className="d-flex gap-1">
                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => recordDepositPayment(item)}>Record payment</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyDepositLink(item)}>Copy pay link</button>
                    <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => waiveDeposit(item)}>Waive</button>
                  </div>
                )])}
            />
          </>
        )}

        {tab === 'exams' && (
          <>
            <form className="d-flex flex-wrap gap-2 align-items-end mb-3" onSubmit={submitExam}>
              {applicantSelect(examForm.applicant_id, e => setExamForm({ ...examForm, applicant_id: e.target.value }))}
              <input required className="form-control form-control-sm" placeholder="Exam name" value={examForm.exam_name} onChange={e => setExamForm({ ...examForm, exam_name: e.target.value })} />
              <input required type="date" className="form-control form-control-sm" value={examForm.exam_date} onChange={e => setExamForm({ ...examForm, exam_date: e.target.value })} />
              <input required className="form-control form-control-sm" placeholder="Subject" value={examForm.subject} onChange={e => setExamForm({ ...examForm, subject: e.target.value })} />
              <input required type="number" step="0.01" className="form-control form-control-sm" placeholder="Score" value={examForm.score} onChange={e => setExamForm({ ...examForm, score: e.target.value })} />
              <input type="number" step="0.01" className="form-control form-control-sm" placeholder="Max score" value={examForm.max_score} onChange={e => setExamForm({ ...examForm, max_score: e.target.value })} />
              <input className="form-control form-control-sm" placeholder="Grade" value={examForm.grade} onChange={e => setExamForm({ ...examForm, grade: e.target.value })} />
              <button className="btn btn-sm btn-primary" type="submit">Record result</button>
            </form>
            <DataTable headers={['Applicant', 'Exam', 'Subject', 'Score', 'Date']} rows={data.exams.map(item => [applicantName(item.applicant_id), item.exam_name, item.subject, `${item.score}/${item.max_score}`, item.exam_date])} />
          </>
        )}
      </div>

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="info"
        input={{ placeholder: '', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </section>
  );
}

function DataTable({ headers, rows }) {
  return (
    <div className="table-responsive">
      <table className="table">
        <thead><tr>{headers.map(header => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody>
      </table>
      {!rows.length && <p className="text-muted mb-0">No records found.</p>}
    </div>
  );
}

export default EnterpriseAdmissionsPanel;
